import { useEffect, useRef } from "react";
import { useMap } from "@vis.gl/react-google-maps";

interface DirectionsRouteProps {
  points: {
    lat: number;
    lng: number;
  }[];
  color?: string;
}

export const DirectionsRoute: React.FC<DirectionsRouteProps> = ({
  points = [],
  color = "#000",
}) => {
  const map = useMap();
  const rendererRef = useRef<google.maps.DirectionsRenderer | null>(null);
  const serviceRef = useRef<google.maps.DirectionsService | null>(null);

  useEffect(() => {
    if (!map || points.length < 2)
      return () => {
        rendererRef.current?.setMap(null);
        rendererRef.current = null;
      };

    if (!rendererRef.current) {
      rendererRef.current = new google.maps.DirectionsRenderer({
        suppressMarkers: true,
        polylineOptions: {
          strokeColor: color,
          strokeWeight: 5,
        },
      });

      rendererRef.current.setMap(map);
    }

    if (!serviceRef.current) {
      serviceRef.current = new google.maps.DirectionsService();
    }

    const origin = points[0];
    const destination = points[points.length - 1];

    const waypoints = points.slice(1, -1).map((point) => ({
      location: point,
      stopover: false,
    }));

    serviceRef.current.route(
      {
        origin,
        destination,
        waypoints,
        travelMode: google.maps.TravelMode.DRIVING,
      },
      (result, status) => {
        if (status === "OK" && result) {
          rendererRef.current?.setDirections(result);
        }
      }
    );

    return () => {
      rendererRef.current?.setMap(null);
      rendererRef.current = null;
    };
  }, [points, color]);

  return null;
};
